"use client";

import type { AvatarState, MouthShape } from "@/lib/interview/v2/avatar";

type Props = {
  avatar: "jobolar-nova" | "jobolar-orion" | "none";
  state: AvatarState;
  mouth: MouthShape;
  customImageUrl?: string;
};

export default function InterviewerAvatar({ avatar, state, mouth, customImageUrl }: Props) {
  if (avatar === "none" && !customImageUrl) return null;
  const isNova = avatar !== "jobolar-orion";
  const mouthGeometry = {
    closed: { rx: 15, ry: 2 },
    wide: { rx: 19, ry: 7 },
    round: { rx: 8, ry: 12 },
    open: { rx: 13, ry: 10 },
  }[mouth];
  return (
    <div className="relative mx-auto w-full max-w-sm" aria-label={`Synthetic JOBolar.Ai interviewer, ${state}`}>
      <div className={`avatar-stage relative aspect-square overflow-hidden rounded-3xl bg-gradient-to-b ${isNova ? "from-indigo-100 to-violet-200" : "from-cyan-100 to-slate-200"} ${state}`}>
        {customImageUrl ? (
          <div
            role="img"
            aria-label="User-provided local avatar"
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${customImageUrl}")` }}
          />
        ) : (
          <svg viewBox="0 0 320 320" className="absolute inset-0 h-full w-full" role="img" aria-label={isNova ? "JOBolar.Ai Nova synthetic interviewer" : "JOBolar.Ai Orion synthetic interviewer"}>
            <defs><linearGradient id="jacket" x1="0" y1="0" x2="1" y2="1"><stop stopColor={isNova ? "#4338ca" : "#0f766e"} /><stop offset="1" stopColor="#172554" /></linearGradient></defs>
            <ellipse cx="160" cy="318" rx="118" ry="92" fill="url(#jacket)" />
            <path d="M126 235 L160 282 L194 235" fill="#f8fafc" />
            <rect x="139" y="211" width="42" height="44" rx="18" fill={isNova ? "#a9684a" : "#87553e"} />
            <ellipse className="avatar-head" cx="160" cy="145" rx="73" ry="91" fill={isNova ? "#bd7c5b" : "#9b6449"} />
            {isNova ? <path d="M89 148 C78 45 123 23 169 31 C233 39 239 110 226 174 C213 128 213 84 172 72 C129 59 111 98 89 148Z" fill="#2b1836" /> : <path d="M92 124 C94 55 124 32 169 36 C211 39 230 73 224 126 C206 91 181 76 148 77 C121 78 104 96 92 124Z" fill="#33251f" />}
            <ellipse cx="132" cy="148" rx="8" ry="6" fill="#172033" /><ellipse cx="188" cy="148" rx="8" ry="6" fill="#172033" />
            <circle className="avatar-eye" cx="134" cy="146" r="2" fill="#fff" /><circle className="avatar-eye" cx="190" cy="146" r="2" fill="#fff" />
            <path d="M151 176 Q160 181 170 176" stroke="#7c3f35" strokeWidth="3" fill="none" strokeLinecap="round" />
            <ellipse cx="160" cy="202" rx={mouthGeometry.rx} ry={mouthGeometry.ry} fill="#5f2430" className="avatar-mouth" />
            {isNova ? <><circle cx="85" cy="174" r="7" fill="#facc15" /><circle cx="235" cy="174" r="7" fill="#facc15" /></> : <path d="M116 217 Q160 237 205 216" stroke="#4b2f26" strokeWidth="8" fill="none" opacity=".55" />}
          </svg>
        )}
        {state === "listening" ? <div className="absolute inset-5 rounded-full border-4 border-blue-500/40 listening-ring" /> : null}
        <div className="absolute bottom-3 left-3 rounded-full bg-slate-950/75 px-3 py-1 text-xs font-semibold text-white">{customImageUrl ? "Local custom avatar" : isNova ? "JOBolar.Ai Nova" : "JOBolar.Ai Orion"} · Synthetic</div>
      </div>
      <p className="mt-2 text-center text-xs text-slate-500">Synthetic presentation layer—not a real interviewer.</p>
      <style jsx>{`
        .avatar-stage.listening svg, .avatar-stage.listening > div[role='img'] { animation: listen 1.8s ease-in-out infinite; }
        .avatar-stage.speaking .avatar-head { transform-origin: 160px 210px; animation: speak 1.1s ease-in-out infinite; }
        .avatar-eye { animation: blink 5s linear infinite; transform-origin: center; }
        .avatar-mouth { transition: rx 80ms ease, ry 80ms ease; }
        .listening-ring { animation: pulse 1.6s ease-out infinite; }
        @keyframes listen { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(2px) rotate(.6deg); } }
        @keyframes speak { 0%,100% { transform: rotate(0); } 50% { transform: rotate(-.5deg); } }
        @keyframes blink { 0%,46%,49%,100% { transform: scaleY(1); } 47%,48% { transform: scaleY(.1); } }
        @keyframes pulse { from { transform: scale(.92); opacity: .8; } to { transform: scale(1.06); opacity: 0; } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; } }
      `}</style>
    </div>
  );
}
